import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { buildUrlWithParams } from "../utils/queryParamStore";
import { BrandLogo } from "./BrandLogo";

// Studio-first footer (Mock 1). Studio links target /studio (Mock 3 landing).
const studioLinks = [
  { label: "Overview", path: "/studio" },
  { label: "How it works", path: "/studio#loop" },
  { label: "9 capabilities", path: "/studio#caps" },
  { label: "Start building", path: "/studio#ship" },
];

const upskillLinks = [
  { label: "Overview", path: "/upskill-plan" },
  { label: "Sample plan", path: "/sample-plan" },
  { label: "Pricing", path: "/pricing" },
  { label: "FAQ", path: "/faq" },
];

const companyLinks = [
  { label: "About", path: "/about" },
  { label: "Contact Us", path: "/contact" },
  { label: "Learn AI Agents", path: "/learn" },
  { label: "Blogs", path: "/blog" },
  { label: "AI Glossary", path: "/glossary" },
];

const legalLinks = [
  { label: "Privacy Policy", path: "/privacy-policy" },
  { label: "Terms of Use", path: "/terms-of-use" },
  { label: "Refund Policy", path: "/refund-policy" },
];

export default function Footer() {
  const { isPaid } = useApp();
  /** Preserve query params on navigation, but keep in-page #anchors intact. */
  const linkTo = (path: string) => (path.includes("#") ? path : buildUrlWithParams(path));
  const columns = [
    { title: "Studio", links: studioLinks },
    { title: "Upskill Plan", links: upskillLinks.filter((link) => !(isPaid && link.path === "/pricing")) },
    { title: "Company", links: companyLinks },
    { title: "Legal", links: legalLinks },
  ];
  const linkStyle: React.CSSProperties = {
    color: "rgba(248, 250, 252, 0.65)",
    fontSize: "14px",
    textDecoration: "none",
    transition: "color 0.2s",
  };

  const headingStyle: React.CSSProperties = {
    color: "#fff",
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  };

  return (
    <footer style={{ marginTop: "auto", background: "var(--navy-deep)" }}>
      <div
        className="container footer-marketing-grid"
        style={{
          padding: "40px 24px 28px",
          display: "grid",
          gridTemplateColumns: "minmax(180px, 1fr) repeat(4, 1fr)",
          gap: "32px",
          alignItems: "start",
          maxWidth: "1120px",
          margin: "0 auto",
        }}
      >
        <div className="footer-brand" style={{ gridColumn: "span 1" }}>
          <Link to="/" style={{ display: "inline-block", marginBottom: "8px" }}>
            <BrandLogo height={52} />
          </Link>
          <p style={{ margin: 0, fontSize: "14px", lineHeight: 1.55, color: "rgba(248, 250, 252, 0.55)", maxWidth: "260px" }}>
            Build a real AI agent while you learn — plus personalised AI upskilling plans for engineers and leaders.
          </p>
        </div>

        {columns.map((column) => (
          <div key={column.title}>
            <h4 style={headingStyle}>{column.title}</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {column.links.map((link) => (
                <Link
                  key={link.path}
                  to={linkTo(link.path)}
                  style={linkStyle}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(248, 250, 252, 0.65)")}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          borderTop: "1px solid rgba(255, 255, 255, 0.08)",
          padding: "16px 24px",
        }}
      >
        <div
          className="container"
          style={{
            maxWidth: "1120px",
            margin: "0 auto",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <span style={{ color: "rgba(248, 250, 252, 0.45)", fontSize: "13px" }}>
            © {new Date().getFullYear()} AI Fluens. All rights reserved.
          </span>
          <span style={{ color: "rgba(248, 250, 252, 0.45)", fontSize: "13px" }}>Be AI Fluent.</span>
        </div>
      </div>

      <div style={{ height: "6px", background: "var(--primary)", width: "100%" }} aria-hidden />

      <style>{`
        @media (max-width: 768px) {
          .footer-marketing-grid {
            grid-template-columns: 1fr 1fr !important;
          }
          .footer-brand {
            grid-column: 1 / -1 !important;
          }
        }
        @media (max-width: 480px) {
          .footer-marketing-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </footer>
  );
}
